import { CloseOutlined, DeleteFilled, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Input, Drawer, Button, Switch, Tooltip, Typography, message, Row, Col } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import useDiscardChangesModal from '../hooks/useDiscardChangesModal';
import usePrefetchPages from '../hooks/usePrefetchPages';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { CandidateSourceOptionCollection, PaginatedResponse } from '../types';
import styles from './CandidateSourceOptionsDrawer.module.css';
import Form from './Form';
import PageTitle from './PageTitle';

interface CandidateSourceOptionsFormValues {
  sourceOptions?: CandidateSourceOptionCollection[];
}

const CandidateSourceOptionsDrawer = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const [setIsDirty] = useDiscardChangesModal(form);

  const candidateSourceOptionsPages = useInfiniteAPIQuery(['/candidate-source-options/']);

  const candidateSourceOptions = usePrefetchPages(
    candidateSourceOptionsPages,
    (data) => data.results
  );

  const mutation = useAPIMutation(
    async (variables: CandidateSourceOptionsFormValues, api) => {
      let candidateSourceOptions: CandidateSourceOptionCollection[] = [];
      let candidateSourceOptionsPage: PaginatedResponse<CandidateSourceOptionCollection> = {
        next: undefined,
        count: 0,
        results: [],
      };

      do {
        candidateSourceOptionsPage = await queryClient.fetchQuery(
          candidateSourceOptionsPage.next ?? ['/candidate-source-options/']
        );

        candidateSourceOptions = candidateSourceOptions.concat(candidateSourceOptionsPage.results);
      } while (candidateSourceOptionsPage.next);

      const validCandidateSourceOptions =
        candidateSourceOptions.filter((formSourceOption) => !formSourceOption.is_protected) || [];

      const validFormSourceOptions =
        variables.sourceOptions?.filter((formSourceOption) => !formSourceOption.is_protected) || [];

      await Promise.all(
        validCandidateSourceOptions.map((sourceOption) => {
          if (
            !validFormSourceOptions?.find(
              (formSourceOption) => formSourceOption.id === sourceOption.id
            )
          ) {
            return api.delete(`/candidate-source-options/${sourceOption.id}/`);
          }
        })
      );

      const fixedCandidateSourceOptionsLength =
        (variables.sourceOptions?.length || 0) - validFormSourceOptions.length;

      await Promise.all(
        validFormSourceOptions.map((formSourceOption, idx) => {
          if (formSourceOption.id) {
            return api.patch(`/candidate-source-options/${formSourceOption.id}/`, {
              name: formSourceOption.name,
              visible: formSourceOption.visible,
              sequence: fixedCandidateSourceOptionsLength + idx,
            });
          } else {
            return api.post(`/candidate-source-options/`, {
              name: formSourceOption.name,
              visible: formSourceOption.visible,
              sequence: fixedCandidateSourceOptionsLength + idx,
            });
          }
        })
      );
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Origens configuradas.'));
        navigate({ pathname: '/:tenant/settings/candidateRegisterFields/', params: { tenant } });
      },
      onSettled: async (_, __) => {
        await queryClient.invalidateQueries('/candidate-source-options/');
      },
    }
  );

  return (
    <Drawer
      className={styles.drawer}
      title={
        <PageTitle
          title={t('Configurar Origens')}
          level={2}
          extra={[
            <Button
              key="close"
              type="text"
              onClick={() =>
                navigate({
                  pathname: '/:tenant/settings/candidateRegisterFields/',
                  params: { tenant },
                })
              }
              shape="circle"
            >
              <CloseOutlined />
            </Button>,
          ]}
        />
      }
      maskClosable
      size="large"
      closable={false}
      width={720}
      footer={
        <Row gutter={[16, 16]}>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isLoading}
              onClick={() => form.submit()}
            >
              <Trans>Salvar</Trans>
            </Button>
          </Col>
          <Col flex={1} />
          <Col>
            <Button
              loading={mutation.isLoading}
              onClick={() =>
                navigate({
                  pathname: '/:tenant/settings/candidateRegisterFields/',
                  params: { tenant },
                })
              }
            >
              <Trans>Fechar</Trans>
            </Button>
          </Col>
        </Row>
      }
      open
      onClose={() =>
        navigate({ pathname: '/:tenant/settings/candidateRegisterFields/', params: { tenant } })
      }
    >
      <Helmet>
        <title>
          {t('Configurar Origens')} | {t('Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<CandidateSourceOptionsFormValues>
        form={form}
        layout="vertical"
        initialValues={{ sourceOptions: candidateSourceOptions }}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
        onValuesChange={() => setIsDirty(form.isFieldsTouched())}
      >
        <Form.List name="sourceOptions">
          {(fields, { add, remove }) => (
            <>
              <Form.Row>
                <Form.Col flex={1}>
                  <Typography.Paragraph>
                    <Trans>Opção</Trans>
                  </Typography.Paragraph>
                </Form.Col>
                <Form.Col>
                  <Typography.Paragraph>
                    <Trans>Visível</Trans>{' '}
                    <Tooltip title={t('Visível no formulário de candidatura')}>
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Typography.Paragraph>
                </Form.Col>
              </Form.Row>
              {fields.map(({ key, name }) => {
                const optionDisabled = form.getFieldValue(['sourceOptions', name, 'is_protected']);
                const slug = form.getFieldValue(['sourceOptions', name, 'slug']);
                return (
                  <Form.Row key={key}>
                    {!optionDisabled && (
                      <Form.Col>
                        <Form.Item style={{ marginBottom: '5%' }}>
                          <Button size="small" type="text" onClick={() => remove(name)}>
                            <DeleteFilled />
                          </Button>
                        </Form.Item>
                      </Form.Col>
                    )}
                    <Form.Col flex={1}>
                      <Form.Item
                        name={[name, 'name']}
                        rules={[
                          {
                            required: true,
                            whitespace: true,
                            message: t('Por favor, insira nome da opção'),
                          },
                        ]}
                        style={{ marginBottom: '5%' }}
                      >
                        <Input
                          disabled={optionDisabled}
                          suffix={
                            slug && (
                              <Tooltip
                                title={t(`Utilize {{slug}} como UTM_SOURCE no portal de vagas.`, {
                                  slug,
                                })}
                              >
                                <InfoCircleOutlined
                                  data-testid={`add-source-option-tooltip-${key}`}
                                />
                              </Tooltip>
                            )
                          }
                          data-testid={`add-source-option-input-${key}`}
                        />
                      </Form.Item>
                    </Form.Col>
                    <Form.Col>
                      <Form.Item
                        name={[name, 'visible']}
                        valuePropName="checked"
                        style={{ marginBottom: '5%' }}
                      >
                        <Switch disabled={optionDisabled} />
                      </Form.Item>
                    </Form.Col>
                  </Form.Row>
                );
              })}
              <Button type="ghost" block icon={<PlusOutlined />} onClick={() => add()}>
                {' '}
                <Trans>Adicionar Opção</Trans>
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Drawer>
  );
};

export default CandidateSourceOptionsDrawer;
